import React, { memo, useState } from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import { Navigate } from 'react-router-dom'
import { useSearchCompanies } from 'vendors/vendorWizard/selectCompanyStep/queries/useCompaniesQuery'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

const classes = {
  popupIndicator: `search-company-popupIndicator`
}

const Root = styled('div')(() => ({
  [`& .${classes.popupIndicator}`]: {
    transform: 'rotate(0deg)'
  }
}))

type VendorWizardSearchCompanyPropsType = {
  onChange: (value: SearchCompaniesResponseType | null) => void
  value: SearchCompaniesResponseType | null
}

const VendorWizardSearchCompany: React.FC<VendorWizardSearchCompanyPropsType> = ({ onChange, value }) => {
  const [searchWord, setSearchWord] = useState<string>('')
  const [searchResult, setSearchResult] = useState<SearchCompaniesResponseType[]>([])
  const { t } = useTranslation()

  // Wizard will only work for FI region until we have to support other regions 11.01.2024
  const { data, isError, isLoading } = useSearchCompanies(searchWord, 'FI')

  React.useEffect(() => {
    if (data && isLoading === false) {
      setSearchResult(data || [])
    }
  }, [data, isLoading])

  const onInputChange = (_event: unknown, value: string): void => {
    setSearchWord(value)
  }

  if (isError) {
    return <Navigate to="/error" replace />
  }

  return (
    <Root>
      <Autocomplete
        autoComplete
        id="select-company"
        filterOptions={(x) => x}
        options={searchResult}
        sx={{ flex: 1, mr: 1 }}
        isOptionEqualToValue={(option, value): boolean => option.businessId === value.businessId}
        value={value}
        onChange={(_event, value) => onChange(value)}
        classes={{
          popupIndicatorOpen: classes.popupIndicator
        }}
        loading={isLoading}
        loadingText={t('searching-for-companies')}
        onInputChange={onInputChange}
        getOptionLabel={(option): string => option.name ?? option.businessId}
        renderInput={(params): React.ReactNode => (
          <TextField
            {...params}
            variant="filled"
            label={t('represented-company')}
            required
            helperText={value?.businessId ?? ''}
          />
        )}
        popupIcon={<SearchIcon />}
        noOptionsText={t('no-results')}
        fullWidth
      />
    </Root>
  )
}

export default memo(VendorWizardSearchCompany)
